import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, LockKeyhole } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Logo } from "@/components/site/Navbar";
import { ThemeToggle } from "@/components/site/ThemeToggle";
import { useAuth } from "@/context/AuthContext";
import { formatErr } from "@/lib/api";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await login(email, password);
      nav(u.role === "admin" ? "/admin" : "/member");
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2" data-testid="login-page">
      <div className="relative hidden lg:block overflow-hidden grain">
        <img src="/images/abstract.jpg" alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-[#050F09]/70" />
        <div className="relative z-10 h-full flex flex-col justify-end p-14 text-white">
          <img src="/images/logo_full.png" alt="I'm The Plug Now" className="w-72 mb-10" data-testid="login-full-logo" />
          <p className="eyebrow text-amber-300">Members & admins</p>
          <h1 className="mt-3 text-5xl font-extrabold tracking-tight">Welcome back to the network.</h1>
          <p className="mt-4 text-white/75 max-w-md">Member access is available only after approval and cash payment.</p>
        </div>
      </div>
      <div className="flex flex-col p-6 sm:p-12">
        <div className="flex items-center justify-between"><Logo /><ThemeToggle /></div>
        <form onSubmit={submit} className="m-auto w-full max-w-sm space-y-5 py-12" data-testid="login-form">
          <div className="h-14 w-14 rounded-2xl bg-primary/15 grid place-items-center"><LockKeyhole className="h-6 w-6 text-primary" /></div>
          <h2 className="text-3xl font-bold">Sign in</h2>
          <Input required type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} className="h-12 rounded-xl" data-testid="login-email-input" />
          <Input required type="password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} className="h-12 rounded-xl" data-testid="login-password-input" />
          <button disabled={busy} className="w-full h-12 rounded-full bg-primary text-primary-foreground font-bold btn-glow hover:-translate-y-0.5 transition-transform disabled:opacity-60 inline-flex items-center justify-center gap-2" data-testid="login-submit-button">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Sign in
          </button>
          <p className="text-sm text-muted-foreground">Not a member yet? <Link to="/apply" className="font-bold text-primary" data-testid="login-apply-link">Apply for membership</Link></p>
        </form>
      </div>
    </div>
  );
}
